import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import RunBaseForm from '@/pages/WorkflowsCatalog/components/run/run-base-form';
import type { FormValues } from '@/pages/WorkflowsCatalog/sections/RunSection';
import type { WorkflowDetail } from '@/pages/WorkflowsCatalog/types';
import { useEffect } from 'react';
import type { UseFormReturn } from 'react-hook-form';

type RunJsonFormProps = {
  form: UseFormReturn<FormValues>;
  workflow: WorkflowDetail;
};

const RunJsonForm = ({ form, workflow }: RunJsonFormProps) => {
  useEffect(() => {
    const { jsonInput, args } = form.getValues();
    if (!jsonInput || (typeof jsonInput === 'string' && jsonInput.trim() === '')) {
      const pretty = JSON.stringify(args ?? {}, null, 2);
      form.setValue('jsonInput', pretty, { shouldDirty: false, shouldValidate: false });
    }
  }, []);

  return (
    <RunBaseForm form={form} workflow={workflow}>
      <FormField
        control={form.control}
        name="jsonInput"
        rules={{
          validate: (v: unknown) => {
            const raw = (typeof v === 'string' ? v : '')?.trim();
            if (raw === '') return 'Invalid JSON';
            try {
              JSON.parse(raw);
              return true;
            } catch (error) {
              return 'Invalid JSON: ' + (error instanceof Error ? error.message : 'Unknown error');
            }
          },
        }}
        render={({ field }) => (
          <FormItem>
            <FormLabel>JSON Input</FormLabel>
            <FormControl>
              <Textarea
                id="jsonInput"
                placeholder="Enter JSON input..."
                className="min-h-[200px] font-mono"
                value={(field.value as string) ?? ''}
                onChange={(e) => {
                  field.onChange(e);
                  const raw = e.target.value.trim();
                  try {
                    const parsed = JSON.parse(raw);
                    form.setValue('args', parsed, { shouldDirty: true });
                  } catch {
                    // Don't set the value if the JSON is invalid
                  }
                }}
              />
            </FormControl>
            <FormDescription>Enter the workflow input parameters as JSON</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </RunBaseForm>
  );
};

export default RunJsonForm;
